import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  HelpCircle, Menu, Headphones, Mic, BookOpen, PenTool, ClipboardCheck,
  TrendingUp, Search, GraduationCap, Shield, FileText, ChevronDown,
  Package, Library, KeySquare, BarChart3, Home as HomeIcon
} from 'lucide-react';
import { MosaicLogoNav } from '@/components/MosaicLogo';
import AccessibilityToggle from '@/components/AccessibilityToggle';
import UserMenu from '@/components/layout/UserMenu';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';

// v6.32.108 — controlled Products dropdown. We hand-roll this rather
// than use Radix's DropdownMenu because in this codebase Radix's
// Trigger onClick was not toggling state on the live preview (likely
// a React-version / Slot ref-forwarding quirk specific to this
// component-tree depth). A useState + useRef outside-click handler
// is a 30-line hand-roll that's bulletproof.
const ProductsDropdown = ({ isStaff, active }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="nav-products-trigger"
        className={`relative px-1 py-2 text-sm font-medium transition-colors inline-flex items-center gap-1 outline-none focus:outline-none ${
          active ? 'text-[#002FA7]' : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        Products <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        {active && (
          <span className="absolute left-0 right-4 -bottom-[18px] h-[2px] bg-[#002FA7]" />
        )}
      </button>

      {open && (
        <div
          role="menu"
          data-testid="nav-products-menu"
          className="absolute left-0 top-full mt-2 w-72 bg-white border border-gray-200 shadow-lg rounded-md py-1 z-50"
        >
          <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
            MOSAIC Product Family
          </div>
          <div className="border-t border-gray-100" />
          <Link
            to={isStaff ? '/teacher?tab=toolkit' : '/features#feature-automated-scoring'}
            data-testid="nav-products-toolkit"
            onClick={() => setOpen(false)}
            className="flex items-start gap-3 px-3 py-2.5 hover:bg-gray-50"
          >
            <PenTool size={18} className="mt-0.5 text-sky-700 shrink-0" />
            <div>
              <div className="font-semibold text-gray-900 text-sm">Teacher Toolkit</div>
              <div className="text-xs text-gray-500 mt-0.5">automated tools for speaking, vocab, writing, workbooks</div>
            </div>
          </Link>
          <Link
            to="/stability-kits"
            data-testid="nav-products-stability-kits"
            onClick={() => setOpen(false)}
            className="flex items-start gap-3 px-3 py-2.5 hover:bg-gray-50"
          >
            <Package size={18} className="mt-0.5 text-indigo-700 shrink-0" />
            <div>
              <div className="font-semibold text-gray-900 text-sm">Student Stability Kits</div>
              <div className="text-xs text-gray-500 mt-0.5">At-home print learning, K-8 · $149/student</div>
            </div>
          </Link>
        </div>
      )}
    </div>
  );
};

// v6.32.108 — desktop nav link with underline active state.
// Compact px-1 + relative positioning lets the 2px underline sit
// under the link text rather than under the whole row.
const DesktopLink = ({ to, active, children, testid, dataTour }) => (
  <Link
    to={to}
    className={`relative px-1 py-2 text-[13px] xl:text-sm font-medium transition-colors whitespace-nowrap ${
      active ? 'text-[#002FA7]' : 'text-gray-600 hover:text-gray-900'
    }`}
    data-testid={testid}
    data-tour={dataTour}
  >
    {children}
    {active && (
      <span className="absolute left-0 right-0 -bottom-[18px] h-[2px] bg-[#002FA7]" />
    )}
  </Link>
);

const MobileLink = ({ to, active, icon: Icon, children, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors border-b border-gray-100 ${
      active ? 'text-[#002FA7] bg-blue-50' : 'text-gray-700 hover:bg-gray-50'
    }`}
    data-testid={`mobile-nav-${to.replace(/\//g, '-').replace(/^-/, '')}`}
  >
    {Icon && <Icon size={18} className={active ? 'text-[#002FA7]' : 'text-gray-400'} />}
    {children}
  </Link>
);

const Navigation = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isExamMode = location.pathname.includes('/exam/') && !location.pathname.includes('/setup');
  const isAuthPage = location.pathname === '/login' || location.pathname === '/auth/callback';

  const close = () => setMobileOpen(false);
  const isStaff = isAuthenticated && ['teacher', 'school_admin', 'district_admin', 'super_admin'].includes(user?.role);
  const isStudent = isAuthenticated && user?.role === 'student';
  const path = location.pathname;

  // useMemo always called; early returns happen below it (Rules-of-Hooks).
  const studentLinks = useMemo(() => ([
    { to: '/my-assessment', label: 'My Assessment', icon: ClipboardCheck, match: (p) => p === '/my-assessment' },
    { to: '/my-feedback', label: 'My Feedback', icon: BookOpen, match: (p) => p === '/my-feedback' },
    { to: '/my-progress', label: 'My Progress', icon: TrendingUp, match: (p) => p === '/my-progress' },
  ]), []);

  if (isExamMode || isAuthPage) return null;
  if (isAuthenticated && user?.role === 'erma_reviewer') return null;

  return (
    <header className="app-header no-print">
      <div className="app-header-content">
        {/* Mobile hamburger — shown below lg:1024px so iPad Mini portrait
            (768px) uses the drawer rather than crowding the desktop row. */}
        <Button
          variant="ghost"
          size="sm"
          className="lg:hidden p-2 mr-1"
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-btn"
        >
          <Menu size={22} />
        </Button>

        <Link to="/" className="app-logo" data-testid="nav-logo-link">
          <MosaicLogoNav />
        </Link>

        {/* Desktop primary nav (left zone) */}
        <nav className="app-nav hidden lg:flex" aria-label="Primary">
          {isStudent && studentLinks.map((item) => (
            <DesktopLink
              key={item.to}
              to={item.to}
              active={item.match(path)}
              testid={`nav-${item.to.replace(/\//g, '-').replace(/^-/, '')}`}
            >
              {item.label}
            </DesktopLink>
          ))}

          {!isStudent && (
            <>
              {/* DISCOVER — marketing & product information */}
              <DesktopLink to="/" active={path === '/'} testid="nav-home">Home</DesktopLink>
              <DesktopLink to="/features" active={path === '/features'} testid="nav-features">Features</DesktopLink>
              <DesktopLink to="/grades" active={path.startsWith('/grades')} testid="nav-exams" dataTour="nav-exams">Exams</DesktopLink>

              {/* Products dropdown — sub-brand programs (Teacher Toolkit, Stability Kits).
                  v6.32.108 final: controlled useState dropdown so the trigger reliably
                  toggles regardless of Radix asChild ref-forwarding quirks. */}
              <ProductsDropdown isStaff={isStaff} active={path === '/stability-kits'} />


              <DesktopLink to="/quote" active={path === '/quote'} testid="nav-quote">Get a Quote</DesktopLink>

              {/* TEACHER WORK — staff-only assessment tools */}
              <DesktopLink to="/screener" active={path.startsWith('/screener')} testid="nav-screener" dataTour="nav-screener">Screener</DesktopLink>
              {isStaff && (
                <>
                  <DesktopLink to="/teacher-key" active={path.startsWith('/teacher-key')} testid="nav-teacher-key" dataTour="nav-teacher-key">Teacher Key</DesktopLink>
                  <DesktopLink to="/resources" active={path === '/resources'} testid="nav-resources" dataTour="nav-resources">Resources</DesktopLink>
                  <DesktopLink to="/results" active={path.startsWith('/results')} testid="nav-results" dataTour="nav-results">Results</DesktopLink>
                </>
              )}
            </>
          )}
        </nav>

        {/* Right zone — utility rail (visually separated from primary
            nav by a vertical rule). Super Admin users reported the
            Accessibility 👁 toggle appearing to overlap the last
            "Results" nav link; the leading divider + the extra left
            padding below are what give the two zones breathing room. */}
        <div className="flex items-center gap-1.5 lg:gap-2 lg:pl-5 lg:ml-3 xl:pl-6 xl:ml-4 lg:border-l lg:border-gray-200 shrink-0">
          <AccessibilityToggle />
          <Link
            to="/guide"
            className="p-2 text-gray-500 hover:text-[#002FA7] transition-colors"
            title="User Guide"
            data-testid="help-icon"
          >
            <HelpCircle size={20} />
          </Link>
          <span className="hidden lg:block w-px h-6 bg-gray-200 mx-1" aria-hidden="true" />
          <UserMenu />
        </div>
      </div>

      {/* Mobile slide-out drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[280px] p-0">
          <SheetHeader className="p-4 border-b border-gray-200">
            <SheetTitle className="text-left">
              <MosaicLogoNav />
            </SheetTitle>
          </SheetHeader>

          <nav className="flex flex-col" data-testid="mobile-nav-drawer">
            {isStudent ? (
              studentLinks.map((item) => (
                <MobileLink key={item.to} to={item.to} active={item.match(path)} icon={item.icon} onClick={close}>
                  {item.label}
                </MobileLink>
              ))
            ) : (
              <>
                <MobileLink to="/" active={path === '/'} icon={HomeIcon} onClick={close}>Home</MobileLink>
                <MobileLink to="/features" active={path === '/features'} icon={Search} onClick={close}>Features</MobileLink>
                <MobileLink to="/grades" active={path.startsWith('/grades')} icon={BookOpen} onClick={close}>Exams</MobileLink>
                <MobileLink to="/stability-kits" active={path === '/stability-kits'} icon={Package} onClick={close}>Stability Kits</MobileLink>
                <MobileLink to="/quote" active={path === '/quote'} icon={FileText} onClick={close}>Get a Quote</MobileLink>
                <MobileLink to="/screener" active={path.startsWith('/screener')} icon={Mic} onClick={close}>Screener</MobileLink>
                {isStaff && (
                  <>
                    <MobileLink to="/teacher-key" active={path.startsWith('/teacher-key')} icon={KeySquare} onClick={close}>Teacher Key</MobileLink>
                    <MobileLink to="/resources" active={path === '/resources'} icon={Library} onClick={close}>Resources</MobileLink>
                    <MobileLink to="/results" active={path.startsWith('/results')} icon={BarChart3} onClick={close}>Results</MobileLink>
                    <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider mt-2">Dashboards</div>
                    <MobileLink to="/teacher" active={path === '/teacher'} icon={GraduationCap} onClick={close}>Teacher Dashboard</MobileLink>
                    {(user?.role === 'school_admin' || user?.role === 'district_admin' || user?.role === 'super_admin') && (
                      <MobileLink to="/admin/school" active={path === '/admin/school'} icon={ClipboardCheck} onClick={close}>School Dashboard</MobileLink>
                    )}
                    {(user?.role === 'district_admin' || user?.role === 'super_admin') && (
                      <MobileLink to="/admin/district" active={path === '/admin/district'} icon={TrendingUp} onClick={close}>District Dashboard</MobileLink>
                    )}
                    {user?.role === 'super_admin' && (
                      <MobileLink to="/superadmin" active={path === '/superadmin'} icon={Shield} onClick={close}>Super Admin</MobileLink>
                    )}
                  </>
                )}
              </>
            )}

            <div className="mt-auto border-t border-gray-200">
              <MobileLink to="/guide" active={path === '/guide'} icon={HelpCircle} onClick={close}>User Guide</MobileLink>
            </div>
          </nav>
        </SheetContent>
      </Sheet>
    </header>
  );
};

export default Navigation;
